// ===== World 3: Socks – BOUNCE AND ELASTIC =====
// Principle: elastic things push back when they are stretched or pressed. More stretch = further/higher.
// Ball → goal: a rolled-up sock with a face → the laundry basket.
import { defineWorld, defineObj } from '../../core/registry';
import { rr, selBox } from '../../core/draw-helpers';
import type { Ctx2D, Gfx, GameBody, ObjData, PartDef, ObjDef, Placement, Reskin, CorePartType, SimState, Vec, WorldDef, WorldMeta, SoundFn } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, DEG } from './shared';

// ---------- constants ----------
// Note: shadows are set inline here (not g.shadowOn/shadowOff): some use another colour (.25),
// and "off" only clears the colour (blur/offset stay), so the shared helpers are not a drop-in.
const INK = '#3B2A5C';
/** Sock ball colours per skin: gradient, stripes, cuff; fuzz = woolly (fuzzy) outline */
interface BallLook { c1: string; c2: string; st: string; cuff: string; fuzz?: boolean }
const BALLS: Record<string, BallLook> = {
  hero: { c1: '#FF97A1', c2: '#E0394A', st: '#FFFFFF', cuff: '#FFE07A' },
  thick: { c1: '#B9C8F2', c2: '#5E74C0', st: '#FFE07A', cuff: '#FFFFFF', fuzz: true },
  helper: { c1: '#B8F0C8', c2: '#37A35B', st: '#FFFFFF', cuff: '#FFFFFF' },
};
/** Slingshot: how far away a clothespin may be (REACH), max pull distance (MAXD) and launch speed per pixel of pull (KV) */
const REACH = 260, MAXD = 240, KV = 0.1;
/** The latest slingshot (used by the clothespin to draw the band in the build phase) */
let SL: Vec | null = null;

interface SlingData extends ObjData { base?: number }
interface SlingState { launched: boolean; peg: Placement | null; hero: GameBody | null; t0: number; d: number; x?: number; y?: number; from?: Vec; d0?: number }
/** Sim state fields shared between the clothespins and the slingshot */
interface SockSim extends SimState { sockPegs?: Placement[]; sockSling?: SlingState }

// ---------- drawing helpers ----------
// Local ink(), not the shared outline(): full INK colour (not .9 alpha), round joins/caps, and no stroke() call.
function ink(ctx: Ctx2D, w?: number) { ctx.lineWidth = w || 3; ctx.strokeStyle = INK; ctx.lineJoin = 'round'; ctx.lineCap = 'round'; }
// striped elastic (the sock's cuff band) from a to b, sagging by "sag" in the middle (or at the contact point "at")
function band(ctx: Ctx2D, ax: number, ay: number, bx: number, by: number, sag: number, at?: number, w?: number) {
  w = w || 12; at = at == null ? 0.5 : at;
  const dx = bx - ax, dy = by - ay, L = Math.hypot(dx, dy) || 1, nx = -dy / L, ny = dx / L;
  const cx = ax + dx * at + nx * sag, cy = ay + dy * at + ny * sag;
  const path = () => { ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(cx, cy); ctx.lineTo(bx, by); };
  ctx.save(); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  path(); ctx.lineWidth = w + 5; ctx.strokeStyle = INK; ctx.stroke();
  path(); ctx.lineWidth = w; ctx.strokeStyle = '#FF6F7D'; ctx.stroke();
  ctx.setLineDash([7, 7]); path(); ctx.lineWidth = Math.max(2, w - 6); ctx.strokeStyle = '#FFFFFF'; ctx.stroke(); ctx.setLineDash([]);
  ctx.restore();
}
// small knob/peg the elastic is stretched between
function knob(ctx: Ctx2D, x: number, y: number, c?: string) {
  ctx.save(); ctx.beginPath(); ctx.arc(x, y, 9, 0, 7); ctx.fillStyle = c || '#F7B267'; ctx.fill(); ink(ctx, 3); ctx.stroke();
  ctx.beginPath(); ctx.arc(x - 2.5, y - 2.5, 3, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.7)'; ctx.fill(); ctx.restore();
}
// wool sock (lying down, thick and knitted). Drawn in local coordinates: length w, height h
function woolSock(ctx: Ctx2D, w: number, h: number, c1: string, c2: string, squash?: number) {
  const s = squash || 0, hh = h - s * 4;
  ctx.save();
  // leg + foot as one soft shape, heel down on the left, toe on the right
  ctx.beginPath();
  ctx.moveTo(-w / 2, -hh / 2); ctx.lineTo(w / 2 - hh / 2, -hh / 2);
  ctx.arc(w / 2 - hh / 2, 0, hh / 2, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(-w / 2 + 34, hh / 2);
  ctx.quadraticCurveTo(-w / 2 + 22, hh / 2 + 12, -w / 2 + 8, hh / 2 + 4);
  ctx.lineTo(-w / 2, hh / 2);
  ctx.closePath();
  const g = ctx.createLinearGradient(0, -hh / 2, 0, hh / 2); g.addColorStop(0, c1); g.addColorStop(1, c2);
  ctx.fillStyle = g; ctx.fill(); ink(ctx, 3); ctx.stroke();
  // knit pattern (small v's)
  ctx.save(); ctx.clip();
  ctx.strokeStyle = 'rgba(255,255,255,.45)'; ctx.lineWidth = 2;
  for (let x = -w / 2 + 22; x < w / 2 - 4; x += 10) for (let y = -hh / 2 + 5; y < hh / 2; y += 8) { ctx.beginPath(); ctx.moveTo(x - 3, y - 2); ctx.lineTo(x, y + 2); ctx.lineTo(x + 3, y - 2); ctx.stroke(); }
  ctx.restore();
  // ribbed cuff (top of the leg) on the left
  ctx.fillStyle = '#FFF4DC'; rr(ctx, -w / 2 - 3, -hh / 2 - 3, 18, hh + 6, 6); ctx.fill(); ink(ctx, 3); ctx.stroke();
  ctx.strokeStyle = 'rgba(59,42,92,.35)'; ctx.lineWidth = 2;
  for (let x = -w / 2 + 2; x < -w / 2 + 14; x += 4) { ctx.beginPath(); ctx.moveTo(x, -hh / 2 + 1); ctx.lineTo(x, hh / 2 - 1); ctx.stroke(); }
  // toe patch
  ctx.beginPath(); ctx.arc(w / 2 - hh / 2, 0, hh / 2 - 1, -Math.PI / 2, Math.PI / 2); ctx.closePath(); ctx.fillStyle = 'rgba(255,255,255,.35)'; ctx.fill();
  // fluff
  ctx.fillStyle = 'rgba(255,255,255,.8)';
  [[-10, -hh / 2 - 4], [18, -hh / 2 - 5], [40, -hh / 2 - 3]].forEach(([x, y]) => { ctx.beginPath(); ctx.arc(x, y, 2.2, 0, 7); ctx.fill(); });
  ctx.restore();
}
// small clothespin holding the hanging wool sock (drawn in the sock's local coordinates: the leg is at x = -65)
function hangPeg(ctx: Ctx2D) {
  ctx.save(); ctx.translate(-72, 0); ctx.rotate(-Math.PI / 2);
  rr(ctx, -6, -4, 12, 24, 3); ctx.fillStyle = '#FFD08A'; ctx.fill(); ink(ctx, 2.5); ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 8, 3, 0, 7); ctx.fillStyle = '#C9C3D6'; ctx.fill();
  ctx.restore();
}
// clothespin (wood with a metal spring). (x,y) = centre, a = direction the pin's "mouth" points (rad)
function peg(ctx: Ctx2D, x: number, y: number, a: number, open: boolean, sel: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  const o = open ? 0.28 : 0.06;
  for (const s of [-1, 1]) {
    ctx.save(); ctx.rotate(s * o);
    rr(ctx, -30, s > 0 ? 0 : -10, 50, 10, 4);
    ctx.fillStyle = s > 0 ? '#E89A4B' : '#FFD08A'; ctx.fill(); ink(ctx, 3); ctx.stroke();
    ctx.restore();
  }
  // spring
  ctx.beginPath(); ctx.arc(-6, 0, 7, 0, 7); ctx.fillStyle = '#C9C3D6'; ctx.fill(); ink(ctx, 3); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-10, -4); ctx.lineTo(-2, 4); ctx.strokeStyle = '#8B84A0'; ctx.lineWidth = 2; ctx.stroke();
  // round selection (not selBox, which is a rounded rectangle)
  if (sel) { ctx.beginPath(); ctx.arc(-4, 0, 36, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.setLineDash([8, 6]); ctx.stroke(); }
  ctx.restore();
}
// clothes hanger (the plank's new look). Drawn centred on x,y with angle a (rad)
function hanger(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, sel: boolean, g: Gfx) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  // small hook behind
  ctx.beginPath(); ctx.moveTo(0, -h / 2 + 2); ctx.lineTo(0, -h / 2 - 10); ctx.arc(7, -h / 2 - 10, 7, Math.PI, Math.PI * 1.9); ctx.strokeStyle = '#8B84A0'; ctx.lineWidth = 4; ctx.lineCap = 'round'; ctx.stroke();
  ctx.restore();
  g.block(ctx, x, y, w, h, a, '#FFD08A', '#D9803E', h / 2, { outline: sel ? '#FFC93C' : null });
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  // shoulders (the hanger's round ends)
  ctx.fillStyle = 'rgba(160,80,30,.35)';
  [-w / 2 + 12, w / 2 - 12].forEach(px => { ctx.beginPath(); ctx.arc(px, 1, 3, 0, 7); ctx.fill(); });
  ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(-w / 2 + 20, -2); ctx.lineTo(w / 2 - 20, -2); ctx.stroke();
  ctx.restore();
}
// ball of yarn (the stone's new look)
function yarn(ctx: Ctx2D, x: number, y: number, a: number, sel: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0);
  ctx.shadowColor = 'rgba(70,35,90,.28)'; ctx.shadowBlur = 10; ctx.shadowOffsetY = 6;
  ctx.beginPath(); ctx.arc(0, 0, 24, 0, 7);
  const gr = ctx.createRadialGradient(-8, -10, 4, 0, 0, 28); gr.addColorStop(0, '#C7B8FF'); gr.addColorStop(1, '#7A5FD6');
  ctx.fillStyle = gr; ctx.fill(); ctx.shadowColor = 'transparent';
  ink(ctx, 3); ctx.stroke();
  ctx.save(); ctx.beginPath(); ctx.arc(0, 0, 23, 0, 7); ctx.clip();
  ctx.strokeStyle = 'rgba(255,255,255,.55)'; ctx.lineWidth = 2.5;
  for (let i = -3; i <= 3; i++) { ctx.beginPath(); ctx.ellipse(i * 7, 0, 6, 26, 0.5, 0, 7); ctx.stroke(); }
  ctx.strokeStyle = 'rgba(59,42,92,.3)';
  for (let i = -2; i <= 2; i++) { ctx.beginPath(); ctx.ellipse(0, i * 9, 26, 5, -0.4, 0, 7); ctx.stroke(); }
  ctx.restore();
  // loose thread
  ctx.beginPath(); ctx.moveTo(18, 14); ctx.quadraticCurveTo(34, 20, 30, 32); ctx.strokeStyle = '#7A5FD6'; ctx.lineWidth = 3; ctx.stroke();
  // round, solid selection (not selBox)
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, 31, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}

// The sock ball (the hero's face).
// Local face, not the shared face()/drawBall(): eyes sit higher (ey -0.18r, er 0.26r) with a thin rim,
// dizzy spiral eyes, a higher mouth, and the stripes/cuff/fuzzy outline of a rolled-up sock.
function sockBall(ctx: Ctx2D, b: GameBody, kind: string, t: number) {
  const { x, y } = b.position, r = b.circleRadius!, v = b.velocity || { x: 0, y: 0 };
  const P = BALLS[(b.skin || (b.def && b.def.skin)) as string] || (kind === 'helper' ? BALLS.helper : BALLS.hero);
  ctx.save(); ctx.translate(x, y);
  // shadow + body
  ctx.shadowColor = 'rgba(70,35,90,.28)'; ctx.shadowBlur = 8; ctx.shadowOffsetY = 5;
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .15, 0, 0, r * 1.05);
  gr.addColorStop(0, '#fff'); gr.addColorStop(0.25, P.c1); gr.addColorStop(1, P.c2);
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); ctx.shadowColor = 'transparent';
  ctx.save(); ctx.rotate(b.roll || 0);
  // stripes and the folded edge (the sock is rolled up)
  ctx.save(); ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.clip();
  ctx.strokeStyle = P.st; ctx.globalAlpha = 0.8; ctx.lineWidth = r * 0.2;
  ctx.beginPath(); ctx.arc(-r * 1.9, 0, r * 1.45, -0.6, 0.6); ctx.stroke();
  ctx.beginPath(); ctx.arc(r * 1.9, 0, r * 1.45, Math.PI - 0.6, Math.PI + 0.6); ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.fillStyle = P.cuff; ctx.beginPath(); ctx.ellipse(0, r * 0.95, r * 1.1, r * 0.42, 0, 0, 7); ctx.fill();
  ctx.strokeStyle = 'rgba(59,42,92,.3)'; ctx.lineWidth = 1.5;
  for (let i = -3; i <= 3; i++) { ctx.beginPath(); ctx.moveTo(i * r * 0.22, r * 0.62); ctx.lineTo(i * r * 0.24, r); ctx.stroke(); }
  ctx.restore();
  // face
  const lx = Math.max(-2.5, Math.min(2.5, v.x * 0.5)), ly = Math.max(-2.5, Math.min(2.5, v.y * 0.5));
  const sp = Math.hypot(v.x, v.y), scared = v.y > 9, fast = sp > 8.5;
  const ex = r * 0.34, ey = -r * 0.18, er = r * 0.26;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * ex, ey, er, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
    ctx.lineWidth = 1.2; ctx.strokeStyle = 'rgba(59,42,92,.5)'; ctx.stroke();
    if ((b.dizzy || 0) > 0) {
      ctx.strokeStyle = INK; ctx.lineWidth = 1.6; ctx.beginPath();
      for (let i = 0; i < 12; i++) { const tt = i / 12 * Math.PI * 3 + t * 0.3, q = i / 12 * er * 0.8; ctx.lineTo(s * ex + Math.cos(tt) * q, ey + Math.sin(tt) * q); }
      ctx.stroke();
    } else { ctx.beginPath(); ctx.arc(s * ex + lx * 0.6, ey + ly * 0.6, er * (scared ? 0.35 : 0.55), 0, 7); ctx.fillStyle = INK; ctx.fill(); }
  }
  ctx.fillStyle = INK; ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round';
  if (b.sad) { ctx.beginPath(); ctx.arc(0, r * 0.5, r * 0.24, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else if (fast || scared) { ctx.beginPath(); ctx.ellipse(0, r * 0.3, r * 0.16, r * 0.2, 0, 0, 7); ctx.fill(); }
  else { ctx.beginPath(); ctx.arc(0, r * 0.12, r * 0.26, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = 'rgba(255,90,120,.45)';
  [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * 0.6, r * 0.12, r * 0.14, r * 0.09, 0, 0, 7); ctx.fill(); });
  ctx.restore();
  // outline (wool = fuzzy edge)
  ctx.beginPath();
  if (P.fuzz) { for (let i = 0; i <= 28; i++) { const a = i / 28 * Math.PI * 2, q = r + (i % 2 ? 1.6 : -0.4); ctx.lineTo(Math.cos(a) * q, Math.sin(a) * q); } }
  else ctx.arc(0, 0, r, 0, 7);
  ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.ellipse(-r * .4, -r * .5, r * .22, r * .13, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fill();
  ctx.restore();
}

// The laundry basket (goal). Local, not the shared basket(): a straight-sided plastic tub with holes and socks sticking up.
function basketBack(ctx: Ctx2D, gl: { x: number; y: number; w: number }, full: number, t: number, g: Gfx) {
  const { x, y, w } = gl;
  ctx.save();
  ctx.shadowColor = 'rgba(70,35,90,.28)'; ctx.shadowBlur = 14; ctx.shadowOffsetY = 7;
  ctx.beginPath(); ctx.moveTo(x - w / 2 - 8, y - 60); ctx.lineTo(x + w / 2 + 8, y - 60); ctx.lineTo(x + w / 2 - 6, y + 8); ctx.lineTo(x - w / 2 + 6, y + 8); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, y - 60, y + 8, '#9FE3FF', '#4FA8E8'); ctx.fill(); ctx.shadowColor = 'transparent';
  ink(ctx, 3); ctx.stroke();
  // holes in the basket
  ctx.fillStyle = 'rgba(40,90,150,.35)';
  for (let row = 0; row < 2; row++) for (let i = -3; i <= 3; i++) {
    const hx = x + i * (w / 8), hy = y - 38 + row * 22;
    g.rr(ctx, hx - 6, hy - 6, 12, 12, 4); ctx.fill();
  }
  // socks sticking up
  const cols = ['#FFC93C', '#37C16B', '#C9B6F2'];
  cols.forEach((c, i) => {
    ctx.save(); ctx.translate(x + (i - 1) * w * 0.28, y - 58); ctx.rotate((i - 1) * 0.35 + Math.sin(t * 0.04 + i) * 0.05);
    g.rr(ctx, -9, -26, 18, 34, 8); ctx.fillStyle = c; ctx.fill(); ink(ctx, 2.5); ctx.stroke();
    ctx.fillStyle = '#fff'; g.rr(ctx, -9, -26, 18, 8, 4); ctx.fill(); ink(ctx, 2); ctx.stroke();
    ctx.restore();
  });
  ctx.restore();
}
function basketFront(ctx: Ctx2D, gl: { x: number; y: number; w: number }, g: Gfx) {
  const { x, y, w } = gl;
  ctx.save();
  g.rr(ctx, x - w / 2 - 12, y - 66, w + 24, 14, 7); ctx.fillStyle = '#7FD3FF'; ctx.fill(); ink(ctx, 3); ctx.stroke();
  // handles
  [-1, 1].forEach(s => { g.rr(ctx, x + s * (w / 2 - 14) - 12, y - 64, 24, 8, 4); ctx.fillStyle = '#2F7FC9'; ctx.fill(); });
  ctx.restore();
}

// The slingshot (fixed machine in the level): trunk + fork
function slingFrame(ctx: Ctx2D, o: { x: number; y: number; base?: number }) {
  const x = o.x, y = o.y, base = o.base || 110;
  ctx.save();
  // trunk + fork (wood)
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  const path = () => { ctx.beginPath(); ctx.moveTo(x, y + base); ctx.lineTo(x, y + 44); ctx.quadraticCurveTo(x - 30, y + 36, x - 30, y); ctx.moveTo(x, y + 44); ctx.quadraticCurveTo(x + 30, y + 36, x + 30, y); };
  path(); ctx.lineWidth = 22; ctx.strokeStyle = INK; ctx.stroke();
  path(); ctx.lineWidth = 16; ctx.strokeStyle = '#E89A4B'; ctx.stroke();
  ctx.beginPath(); ctx.moveTo(x - 3, y + base - 6); ctx.lineTo(x - 3, y + 50); ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(255,255,255,.45)'; ctx.stroke();
  // foot
  rr(ctx, x - 26, y + base - 6, 52, 14, 6); ctx.fillStyle = '#D9803E'; ctx.fill(); ink(ctx, 3); ctx.stroke();
  ctx.restore();
}
function slingBand(ctx: Ctx2D, o: Vec, px: number, py: number, r?: number) {
  // two bands from the fork tips to the "pocket" at (px,py)
  const L = [o.x - 30, o.y - 2], R = [o.x + 30, o.y - 2];
  band(ctx, L[0], L[1], px, py, 0, 1, 9);
  band(ctx, R[0], R[1], px, py, 0, 1, 9);
  // the pocket (a sock heel)
  ctx.save(); ctx.beginPath(); ctx.arc(px, py, (r || 15) + 4, 0, 7); ctx.fillStyle = '#FFE07A'; ctx.fill(); ink(ctx, 3); ctx.stroke(); ctx.restore();
}

// ---------- skins (statics, ball, goal, background) ----------
const skins: WorldDef['skins'] = {
  // shelf = drawer edge (green like the chest of drawers)
  socks_shelf: { c1: '#B4E2BD', c2: '#6FB782', r: 9 },
  // floor = wooden floor
  socks_floor(ctx: Ctx2D, b: GameBody, g: Gfx) {
    g.block(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, '#F0C593', '#D9A56E', 8);
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(160,100,50,.35)'; ctx.lineWidth = 2;
    for (let x = -b.dw! / 2 + 60; x < b.dw! / 2 - 10; x += 110) { ctx.beginPath(); ctx.moveTo(x, -b.dh! / 2 + 3); ctx.lineTo(x, b.dh! / 2 - 2); ctx.stroke(); }
    ctx.restore();
  },
  // wall = side of the chest of drawers (white/cream)
  socks_side: { c1: '#FFFFFF', c2: '#E3D8F5', r: 10 },
  // drawer: green front with a white handle
  socks_drawer(ctx: Ctx2D, b: GameBody, g: Gfx) {
    g.block(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, '#9DD6A8', '#5FAE73', 10);
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    if (b.dh! >= 40) { g.rr(ctx, -24, -2, 48, 12, 6); ctx.fillStyle = '#fff'; ctx.fill(); ink(ctx, 2.5); ctx.stroke(); }
    ctx.restore();
  },
  // hard table / stool (red)
  socks_stool: { c1: '#FFB3B8', c2: '#E0616D', r: 10 },
};

const ball: WorldDef['ball'] = (ctx, b, kind, t) => { sockBall(ctx, b, kind, t); };

const goal: WorldDef['goal'] = {
  bowl: basketBack,
  front: basketFront,
  bell(ctx: Ctx2D, gl: { x: number; y: number; hitT?: number }, t: number, g: Gfx) {
    // bell hanging on a string from above
    const sw = gl.hitT != null ? Math.sin((t - gl.hitT) * 0.35) * 0.5 : Math.sin(t * 0.04) * 0.08;
    ctx.save(); ctx.translate(gl.x, gl.y - 60);
    ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(0, -40); ctx.lineTo(0, 0); ctx.stroke();
    ctx.rotate(sw);
    ctx.shadowColor = 'rgba(70,35,90,.28)'; ctx.shadowBlur = 10; ctx.shadowOffsetY = 5;
    ctx.beginPath(); ctx.arc(0, 60, 28, 0, 7); ctx.fillStyle = g.lg(ctx, 32, 88, '#FFE07A', '#F2A516'); ctx.fill(); ctx.shadowColor = 'transparent';
    ink(ctx, 3); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-27, 58); ctx.lineTo(27, 58); ctx.stroke();
    ctx.beginPath(); ctx.arc(0, 70, 6, 0, 7); ctx.fillStyle = INK; ctx.fill();
    ctx.beginPath(); ctx.moveTo(0, 76); ctx.lineTo(0, 86); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(-10, 48, 6, 4, -0.5, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.7)'; ctx.fill();
    // bow
    ctx.fillStyle = '#FF5A5F';
    [-1, 1].forEach(s => { ctx.beginPath(); ctx.moveTo(0, 30); ctx.lineTo(s * 16, 22); ctx.lineTo(s * 16, 38); ctx.closePath(); ctx.fill(); ctx.stroke(); });
    ctx.restore();
  },
};

const bgExtra: WorldDef['bgExtra'] = (ctx, W, H, level, g) => {
  // soft shadow along the floor
  const gr = ctx.createLinearGradient(0, H - 90, 0, H); gr.addColorStop(0, 'rgba(217,165,110,0)'); gr.addColorStop(1, 'rgba(217,165,110,.25)');
  ctx.fillStyle = gr; ctx.fillRect(0, H - 90, W, 90);
  // small notice board with a sock ball (covers the background's text – the game has no text)
  ctx.save(); ctx.globalAlpha = 0.9; ctx.translate(990, 335); ctx.rotate(0.06);
  g.rr(ctx, -110, -46, 220, 92, 14); ctx.fillStyle = '#F4ECFF'; ctx.fill(); ink(ctx, 3); ctx.stroke();
  g.rr(ctx, -98, -34, 196, 68, 10); ctx.fillStyle = '#FFE9F0'; ctx.fill();
  ctx.strokeStyle = '#FF9FB0'; ctx.lineWidth = 4; ctx.setLineDash([2, 10]); ctx.beginPath(); ctx.moveTo(-70, 18); ctx.quadraticCurveTo(-20, -40, 20, 10); ctx.quadraticCurveTo(40, 30, 60, 0); ctx.stroke(); ctx.setLineDash([]);
  ctx.beginPath(); ctx.arc(70, -4, 18, 0, 7); ctx.fillStyle = '#FF97A1'; ctx.fill(); ink(ctx, 2.5); ctx.stroke();
  ctx.fillStyle = INK; [-1, 1].forEach(k => { ctx.beginPath(); ctx.arc(70 + k * 6, -7, 2.6, 0, 7); ctx.fill(); });
  ctx.beginPath(); ctx.arc(-70, 18, 7, 0, 7); ctx.fillStyle = '#FFC93C'; ctx.fill(); ink(ctx, 2); ctx.stroke();
  ctx.restore();
};

// ---------- parts ----------
const parts: Record<string, PartDef> = {
  // ---- Sock elastic stretched between two knobs: a trampoline ----
  // It gives the ball its speed back (no extra force): the further the ball falls, the higher it bounces.
  socks_elastic: {
    rects: [{ x: 0, y: 0, w: 140, h: 12 }], rot: 15, minA: -60, maxA: 60, defaultA: 0,
    draw(ctx, pl, sel) {
      const a = (pl.a || 0) * DEG, c = Math.cos(a), s = Math.sin(a);
      const ax = pl.x - c * 70, ay = pl.y - s * 70, bx = pl.x + c * 70, by = pl.y + s * 70;
      band(ctx, ax, ay, bx, by, 0, 0.5, 12); knob(ctx, ax, ay); knob(ctx, bx, by);
      if (sel) { ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); selBox(ctx, -84, -18, 168, 36, 14, true); ctx.restore(); }
    },
    icon(ctx) { band(ctx, -44, 6, 44, 6, 0, 0.5, 12); knob(ctx, -44, 6); knob(ctx, 44, 6); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG;
      const b = api.add(api.R(pl.x, pl.y, 140, 12, { isStatic: true, angle: a, friction: 0.02, restitution: 0.3, chamfer: { radius: 5 }, label: 'socks_elastic' }));
      b.kind = 'socks_elastic'; b.plA = a; b.padPower = 1.02; b.padMin = 3;
      api.S.pads.add(b);
      const st = { hitT: -99, at: 0.5, amp: 0 };
      api.onHit(b, o => {
        if (!api.isBall(o) && !api.isDynamic(o)) return;
        const c = Math.cos(a), s = Math.sin(a), lx = (o.position.x - pl.x) * c + (o.position.y - pl.y) * s;
        st.hitT = api.S.t; st.at = Math.max(0.12, Math.min(0.88, 0.5 + lx / 140)); st.amp = Math.min(22, 4 + o.speed * 1.6);
      });
      return {
        layer: 'mid',
        draw(ctx, t, g, S) {
          const c = Math.cos(a), s = Math.sin(a);
          const ax = pl.x - c * 70, ay = pl.y - s * 70, bx = pl.x + c * 70, by = pl.y + s * 70;
          const k = S.t - st.hitT, sag = k < 40 ? st.amp * Math.exp(-k / 9) * Math.cos(k * 0.45) : 0;
          band(ctx, ax, ay, bx, by, sag, st.at, 12); knob(ctx, ax, ay); knob(ctx, bx, by);
        },
      };
    },
    intro(ctx, t, g, h) {
      const k = (t % 120) / 120, y = 60 + 150 * (1 - Math.abs(Math.cos(k * Math.PI)));
      const touch = y > 196, sag = touch ? (y - 196) : 0;
      band(ctx, 160, 220, 300, 220, sag, 0.5, 12); knob(ctx, 160, 220); knob(ctx, 300, 220);
      g.ball(ctx, h.fakeBall(230, y - 12 + sag * 0.6, 0, 0), 'hero', t);
      if (touch) h.arrow(230, 150, 0, 50, true);
    },
  },

  // ---- Wool sock: soft and "dead" – it swallows the bounce ----
  socks_wool_sock: {
    rects: [{ x: 0, y: 0, w: 130, h: 22 }], rot: 15, minA: -45, maxA: 135, defaultA: 90,
    create(api, pl) {
      const a = (pl.a || 0) * DEG, n = { x: Math.sin(a), y: -Math.cos(a) };
      const b = api.add(api.R(pl.x, pl.y, 130, 22, { isStatic: true, angle: a, friction: 0.5, restitution: 0, chamfer: { radius: 8 }, label: 'socks_wool_sock' }));
      b.kind = 'socks_wool_sock'; b.drag = 0.93; api.S.drags.add(b);
      const pend: GameBody[] = [], st = { sqT: -99 };
      api.onHit(b, o => {
        if (!api.isDynamic(o)) return;
        pend.push(o);
        if (o.speed > 2 && api.S.t - st.sqT > 15) { st.sqT = api.S.t; api.sfx('socks_puff', o.position.x, o.position.y, '#FFF4DC', 6); }
      });
      return {
        layer: 'mid',
        // wool swallows the bounce: speed into the sock vanishes, only a little speed along the sock is left
        after() {
          for (const o of pend.splice(0)) {
            const v = o.velocity, vn = v.x * n.x + v.y * n.y, vt = { x: v.x - vn * n.x, y: v.y - vn * n.y };
            api.Body.setVelocity(o, { x: vt.x * 0.6, y: vt.y * 0.6 });
          }
        },
        draw(ctx, t, g, S) {
          const k = S.t - st.sqT, sq = k < 14 ? 1 - k / 14 : 0;
          ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a);
          ctx.shadowColor = 'rgba(70,35,90,.25)'; ctx.shadowBlur = 8; ctx.shadowOffsetY = 4;
          woolSock(ctx, 130, 22, '#FFB25B', '#E0782E', sq);
          if (Math.abs((pl.a || 0) - 90) <= 30) hangPeg(ctx);
          ctx.restore();
        },
      };
    },
    draw(ctx, pl, sel) {
      ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate((pl.a || 0) * DEG);
      ctx.shadowColor = 'rgba(70,35,90,.25)'; ctx.shadowBlur = 8; ctx.shadowOffsetY = 4;
      woolSock(ctx, 130, 22, '#FFB25B', '#E0782E');
      if (Math.abs((pl.a || 0) - 90) <= 30) hangPeg(ctx);
      if (sel) selBox(ctx, -74, -22, 148, 44, 14, true);
      ctx.restore();
    },
    icon(ctx) { ctx.save(); ctx.rotate(-0.2); woolSock(ctx, 96, 22, '#FFB25B', '#E0782E'); ctx.restore(); },
    intro(ctx, t, g, h) {
      const k = t % 120, y = k < 40 ? 50 + 0.1 * k * k : 196;
      const sq = k >= 40 && k < 52 ? 1 - (k - 40) / 12 : 0;
      ctx.save(); ctx.translate(230, 220); woolSock(ctx, 130, 22, '#FFB25B', '#E0782E', sq); ctx.restore();
      g.ball(ctx, h.fakeBall(230, Math.min(y, 196), 0, 0), 'hero', t);
      if (k > 50 && k < 100) { ctx.fillStyle = 'rgba(255,255,255,.9)'; [[-30, -6], [32, -4], [0, -24]].forEach(([dx, dy]) => { ctx.beginPath(); ctx.arc(230 + dx * (1 + (k - 50) / 60), 196 + dy - (k - 50) * 0.2, 5, 0, 7); ctx.fill(); }); }
    },
  },

  // ---- Clothespin: holds the slingshot's pocket back. The further away, the more stretch – the further the sock flies ----
  socks_clothespin: {
    circle: 22, rot: false,
    draw(ctx, pl, sel) {
      let a = Math.PI;
      if (SL) {
        const d = Math.hypot(SL.x - pl.x, SL.y - pl.y);
        a = Math.atan2(SL.y - pl.y, SL.x - pl.x);
        if (d < REACH) { band(ctx, SL.x - 30, SL.y - 2, pl.x, pl.y, 0, 1, 9); band(ctx, SL.x + 30, SL.y - 2, pl.x, pl.y, 0, 1, 9); ctx.save(); ctx.beginPath(); ctx.arc(pl.x + Math.cos(a) * 14, pl.y + Math.sin(a) * 14, 12, 0, 7); ctx.fillStyle = '#FFE07A'; ctx.fill(); ink(ctx, 3); ctx.stroke(); ctx.restore(); }
      }
      peg(ctx, pl.x, pl.y, a + Math.PI, false, sel);
    },
    icon(ctx) { peg(ctx, 0, 0, -0.6, false, false); },
    create(api, pl) {
      const S = api.S as SockSim;
      (S.sockPegs = S.sockPegs || []).push(pl);
      return {
        layer: 'back',
        draw(ctx, t, g, S) {
          const o = (S as SockSim).sockSling; const a = o ? Math.atan2(o.y! - pl.y, o.x! - pl.x) : Math.PI;
          peg(ctx, pl.x, pl.y, a + Math.PI, !!(o && o.launched && o.peg === pl), false);
        },
      };
    },
    intro(ctx, t, g, h) {
      const o = { x: 300, y: 130, base: 120 };
      slingFrame(ctx, o);
      const k = t % 160;
      const pull = k < 60 ? h.ease(k / 60) : 1;
      const px = 300 - 120 * pull, py = 130 + 60 * pull;
      let bx = px, by = py;
      if (k >= 80) { const f = (k - 80); bx = px + f * 9; by = py - f * 5.5 + 0.12 * f * f; }
      if (k < 84) slingBand(ctx, o, k < 80 ? px : 300, k < 80 ? py : 128, 15);
      else { band(ctx, 270, 128, 330, 128, Math.sin(k) * 3, 0.5, 9); }
      if (k < 80) peg(ctx, px - 30, py + 10, Math.atan2(py - 130, px - 300), false, false);
      else peg(ctx, px - 30, py + 10, Math.atan2(py - 130, px - 300), true, false);
      if (bx < 470) g.ball(ctx, h.fakeBall(bx, by, k >= 80 ? 8 : 0, 0), 'hero', t);
      if (k > 20 && k < 70) h.arrow(250, 250, 0, 40, false);
    },
  },
};

// ---------- objects ----------
const objs: Record<string, ObjDef> = {
  // Slingshot. o: { x, y } = where the pocket rests (between the fork tips), base = length of the trunk
  socks_slingshot: defineObj<SlingState>({
    layer: 'back',
    blockers(o) { return [{ x: o.x, y: o.y + 20, r: 44 }, { x: o.x, y: o.y + ((o as SlingData).base || 110) - 20, r: 26 }]; },
    create(api, o) {
      SL = { x: o.x, y: o.y };
      const S = api.S as SockSim, st: SlingState = { launched: false, peg: null, hero: null, t0: 0, d: 0 };
      // the hero lying in the pocket
      let best: { h: GameBody; d: number } | null = null;
      for (const h of S.heroes) { const d = Math.hypot(h.position.x - o.x, h.position.y - o.y); if (d < 40 && (!best || d < best.d)) best = { h, d }; }
      st.hero = best && best.h;
      // the nearest clothespin within reach pulls the pocket back
      for (const p of S.sockPegs || []) { const d = Math.hypot(p.x - o.x, p.y - o.y); if (d < REACH && (!st.peg || d < st.d)) { st.peg = p; st.d = d; } }
      if (st.hero && st.peg) api.Body.setPosition(st.hero, { x: st.peg.x + (o.x - st.peg.x) / st.d * 14, y: st.peg.y + (o.y - st.peg.y) / st.d * 14 });
      S.sockSling = st; st.x = o.x; st.y = o.y;
      return st;
    },
    start(api, st) {
      const o = st.o, h = st.hero;
      if (!h || !st.peg) return;
      const dx = o.x - h.position.x, dy = o.y - h.position.y, d = Math.hypot(dx, dy) || 1;
      const m0 = Math.PI * 15 * 15 * 0.002, v = KV * Math.min(d, MAXD) * Math.sqrt(m0 / h.mass);
      api.Body.setVelocity(h, { x: dx / d * v, y: dy / d * v });
      st.launched = true; st.t0 = api.S.t; st.from = { x: h.position.x, y: h.position.y }; st.d0 = d;
      api.sfx('socks_twang', o.x, o.y, '#FF6F7D', 6);
    },
    step(api, st) {
      // without a clothespin the sock stays in the pocket
      if (!st.launched && st.hero) { api.Body.setPosition(st.hero, { x: st.o.x, y: st.o.y }); api.Body.setVelocity(st.hero, { x: 0, y: 0 }); }
    },
    draw(ctx, st, t, g, S) {
      const o = st.o as SlingData;
      slingFrame(ctx, o);
      const h = st.hero;
      if (!S.started) {
        // in the build phase the clothespin draws the band (so it follows the finger); without a clothespin the pocket rests in the fork
        if (!st.peg || !h) slingBand(ctx, o, o.x, o.y + 2, h ? h.circleRadius : 15);
        return;
      }
      if (h && (!st.launched || (Math.hypot(h.position.x - st.from!.x, h.position.y - st.from!.y) < st.d0! && S.t - st.t0 < 30))) slingBand(ctx, o, h.position.x, h.position.y, h.circleRadius);
      else {
        const k = S.t - st.t0, w = k < 40 ? Math.sin(k * 0.8) * 10 * Math.exp(-k / 10) : 0;
        band(ctx, o.x - 30, o.y - 2, o.x + 30, o.y - 2, w, 0.5, 9);
      }
    },
  }),
};

// Forget the last slingshot in levels without one (so a clothespin does not draw a band to it)
const setup: WorldDef['setup'] = (api, level) => {
  if (!(level.objs || []).some(o => o.t === 'socks_slingshot')) SL = null;
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  socks_twang: (tone) => { tone(160, 0.35, 'triangle', 0.35, 620); tone(320, 0.2, 'sine', 0.15, 900, 0.02); },
  socks_puff: (tone, noise) => { noise(0.18, 0.25, 500, 0.8); tone(140, 0.12, 'sine', 0.15, 90); },
  socks_boing: (tone) => { tone(200, 0.3, 'sine', 0.3, 520); tone(410, 0.18, 'triangle', 0.12, 260, 0.05); },
};

// ---------- reskins (the kit's parts dressed as socks things: hanger, ball of yarn, shoebox seesaw) ----------
const reskin: Partial<Record<CorePartType, Reskin>> = {
  plank(ctx, pl, sel, g, rs) { const r = rs[0]; hanger(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); },
  long_plank(ctx, pl, sel, g, rs) { const r = rs[0]; hanger(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); },
  stone(ctx, pl, sel) { yarn(ctx, pl.x, pl.y, (pl.a || 0) * DEG, sel); },
  seesaw(ctx, pl, sel, g) {
    const x = pl.x, y = pl.y, a = (pl.a || 0) * DEG, body = pl.body as GameBody | undefined, bp = body ? body.position : { x, y };
    // shoebox as the foot
    ctx.save(); g.rr(ctx, x - 30, y + 4, 60, 44, 6); ctx.fillStyle = '#FF9FB0'; ctx.fill(); ink(ctx, 3); ctx.stroke();
    g.rr(ctx, x - 34, y + 2, 68, 12, 5); ctx.fillStyle = '#FFC4CF'; ctx.fill(); ink(ctx, 3); ctx.stroke(); ctx.restore();
    g.block(ctx, bp.x, bp.y, 230, 14, a, '#FFD08A', '#D9803E', 7);
    ctx.save(); ctx.beginPath(); ctx.arc(x, y, 7, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ink(ctx, 3); ctx.stroke(); ctx.restore();
  },
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { hanger(ctx, 0, 6, -0.35, 100, 16, false, g); },
  long_plank(ctx, g) { hanger(ctx, 0, 6, -0.35, 110, 14, false, g); },
  stone(ctx) { yarn(ctx, 0, 0, 0, false); },
};

// ---------- meta ----------
const meta: WorldMeta = {
  icon: '🧦', strings, music: 'socks.mp3',
  credits: { title: 'Sneaky Tiptoe Music', artist: 'alex-morgan', id: 579470, url: 'https://pixabay.com/music/cartoons-sneaky-tiptoe-music-579470/' },
  synth: { bpm: 84, root: 293.7, sc: 'dorian', prog: [0, 3, 0, 4], bar: 4, lead: 'pizz', dens: 0.45 },
};

const world = defineWorld({
  k: K,
  meta,
  scene,
  wash: 'rgba(250,244,255,.58)',
  bgExtra,
  skins,
  ball,
  goal,
  sounds,
  setup,
  reskin,
  icons,
  parts,
  objs,
  levels,
});

export default world;
